<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import {
  ref,
  reactive,
  computed,
  nextTick,
  onMounted,
  watch,
  type ComponentPublicInstance,
} from "vue";
import {
  AtomText,
  AtomIntersection,
  SatMenuNavigator,
  TptMenuCategory,
  AtomLoadingSpinner,
  useToast,
  useImage,
} from "@chatfood/design-system";
import {
  isBasketRepoError,
  isMenuRepoError,
  isOutletRepoError,
} from "@chatfood/dbe-client/errors";
import type { IMenuHandlerProps } from "./props";
import { useLocale } from "~/composables/use-locale";
import { useBasketStore } from "~/stores/basket";
import { useNavigation } from "~/composables/use-navigation";
import { useOutletStore } from "~/stores/outlet";
import { useFeatureMatrixStore } from "~/stores/featureMatrix";
import { useBusinessStore } from "~/stores/business";
import { useItemSchedulesStore } from "~/stores/itemSchedules";
import type { IDomainMoney } from "~/domain/money";
import { useAnalytics, useBugReporter, useDirectBackend } from "#imports";
import { useSticky } from "~/composables/use-sticky";
import { useHeaderHeight } from "~/composables/use-header-height";
import { useMinimumLeadTime } from "~/composables/use-minimum-lead-time";
import { useServiceError } from "~/composables/use-service-error";
import { friendlyTimeDuration } from "~/utils/time-duration";
import { getAvailableAt } from "~/utils/scheduling";

const css = bemBuilder("menu-handler");

const props = defineProps<IMenuHandlerProps>();

const business = useBusinessStore();
const outlet = useOutletStore();
const basket = useBasketStore();
const featureMatrix = useFeatureMatrixStore();
const itemSchedulesStore = useItemSchedulesStore();

const { t } = useLocale();
const { report } = useBugReporter();
const { toastError, toastUnexpectedError } = useToast();
const { optimize } = useImage();
const { replace } = useNavigation();
const { headerHeightPixel, headerHeight } = useHeaderHeight();
const { isSticky, elBottomOffset: navWrapperBottomOffset } = useSticky(
  "navWrapper",
  headerHeight,
);
const { handleBusinessRuleError } = useServiceError();

const isLoading = ref(true);

const { outletRepo } = useDirectBackend();
const { calculateConflictsFromSchedules } = useMinimumLeadTime();

type GetMenuParams = Parameters<typeof outletRepo.getMenu>[0];
type GetMenuResponse = Awaited<ReturnType<typeof outletRepo.getMenu>>;

const menu = ref<GetMenuResponse>();

const menuItemsMinimumLeadTimeConflicts = computed(() => {
  const schedules = itemSchedulesStore.schedulesByOutlet[props.outletId] || [];
  if (!schedules.length) return {};

  return calculateConflictsFromSchedules(schedules, basket.state.scheduledTime);
});

const getMenu = async () => {
  isLoading.value = true;

  try {
    const availableAt = getAvailableAt(basket.state.scheduledTime);

    const getMenuParams: GetMenuParams = {
      outletId: props.outletId,
      menuSlug: props.menuSlug,
      salesChannel: business.salesChannel,
      availableAt: availableAt ?? new Date().toISOString(),
    };

    itemSchedulesStore.loadSchedules(props.outletId);

    const menuResponse = await outletRepo.getMenu(getMenuParams);

    menu.value = menuResponse;
  } catch (error: unknown) {
    replace("outlet", {
      orderingMode: props.orderingMode,
      outletSlug: outlet.slug,
    });

    if (
      (isOutletRepoError(error) || isMenuRepoError(error)) &&
      !error.isNotFound()
    ) {
      report(error);
    }
  } finally {
    isLoading.value = false;
  }
};

onMounted(async () => {
  await getMenu();
});

watch(
  () => basket.state.scheduledTime,
  async (current, previous) => {
    // Safe deep comparison to avoid JSON.stringify recursion issues
    if (
      (!current && !previous) ||
      (current &&
        previous &&
        current.day === previous.day &&
        current.time === previous.time)
    ) {
      return;
    }

    await getMenu();
  },
);

const toastAndReport = (error: unknown) => {
  report(error);

  const errorMesssage =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : undefined;

  if (errorMesssage) {
    toastError(errorMesssage);
  } else {
    toastUnexpectedError();
  }
};

const handleItemError = (error: unknown) => {
  if (isBasketRepoError(error) && error.isBusinessRule()) {
    handleBusinessRuleError(error);
    return;
  }

  toastAndReport(error);
};

const querySearch = ref("");
const openStates = reactive<Record<string, boolean>>({});

const normalizeForSearch = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036F]/g, "");

const discountedProducts = computed<Record<string, IDomainMoney>>(() => {
  const discount = basket.discount;

  if (!discount || !menu.value) return {};

  if (discount.unit !== "percentage") return {};

  const items = menu.value.categories.map((category) => category.items).flat();

  const products = discount.discountedProducts;
  const discountAmount = discount.amount;

  return items.reduce(
    (acc, item) => {
      const isItemDiscounted =
        products.length === 0 || products.includes(item.plu);
      const price = !isItemDiscounted
        ? item.price
        : {
            value: item.price.value * (1 - discountAmount / 100),
            currency: item.price.currency,
          };

      return {
        ...acc,
        [item.plu]: price,
      };
    },
    {} as Record<string, IDomainMoney>,
  );
});

const menuFiltered = computed(() => {
  const query = normalizeForSearch(querySearch.value);
  const showOutOfStock = featureMatrix.isToggleEnabled(
    "display-items-out-of-stock",
  );

  return menu.value?.categories
    ? menu.value.categories
        .map((category) => {
          const items = category.items
            .filter((item) => {
              const showItem = showOutOfStock || item.inStock;
              return showItem && normalizeForSearch(item.name).includes(query);
            })
            .map((item) => {
              const getMaxItemQuantity = () => {
                const basketItemMax =
                  basket.itemLimits?.[item.plu]?.quantity.maximum;
                const itemMax = item.maxSelection;

                return basketItemMax ?? itemMax ?? undefined;
              };

              const minimumLeadTimeInMinutes =
                menuItemsMinimumLeadTimeConflicts.value?.[item.plu] ?? 0;

              const isDisabled = minimumLeadTimeInMinutes > 0;

              const friendlyTimeInAdvance = friendlyTimeDuration({
                minutes: minimumLeadTimeInMinutes,
                translationConfig: { t },
              });

              const warningMessage = isDisabled
                ? t("components.menu_handler.minimum_lead_time_warning", {
                    timeIndAdvance: friendlyTimeInAdvance,
                  })
                : undefined;

              return {
                id: item.id,
                plu: item.plu,
                name: item.name,
                price: discountedProducts.value[item.plu] ?? item.price,
                originalPrice: item.price,
                amount: basket.itemsSummary[item.id] ?? 0,
                maxItemQuantity: getMaxItemQuantity(),
                link: "",
                inStock: item.inStock,
                isCombo: item.isCombo,
                description: item.description,
                photo: item.photo
                  ? optimize(item.photo, {
                      h: 300,
                      w: 300,
                      fit: "crop",
                      crop: "center",
                    })
                  : undefined,
                hasAllergenItems: item.allergens.length > 0,
                consumables: item.consumables,
                warningMessage,
                isDisabled,
              };
            });

          return { ...category, items };
        })
        .filter((category) => category.items?.length)
    : [];
});

const menuPills = computed(() => {
  return menuFiltered.value.map((category) => ({
    id: category.id,
    name: category.name,
    count: category.items.length,
  }));
});

function expandAllVisible() {
  menuFiltered.value.forEach((cat) => {
    openStates[cat.id] = true;
  });
}

watch(() => menu.value?.id, expandAllVisible, { immediate: true });
watch(querySearch, expandAllVisible);

const pillCategoryActiveId = ref();
const categoryElMap = new Map<string, HTMLElement>();
const isScrolling = ref(false);

function onEnter(entry: Element) {
  if (!isScrolling.value) pillCategoryActiveId.value = entry.id;
}

function categoryRef(id: string) {
  return (inst: Element | ComponentPublicInstance | null) => {
    if (inst && "$el" in inst) categoryElMap.set(id, inst.$el);
    else categoryElMap.delete(id);
  };
}

function scrollToSection(id: string) {
  const element = categoryElMap.get(id);
  if (!element) return;
  openStates[id] = true;
  isScrolling.value = true;
  const top = element.getBoundingClientRect().top;

  nextTick(() => {
    window.scrollTo({
      top: top + window.scrollY - navWrapperBottomOffset.value - 7,
      behavior: "smooth",
    });
  });

  setTimeout(() => {
    isScrolling.value = false;
  }, 1000);
}

const { trackEvent } = useAnalytics();

const trackAddItem = (
  id: string,
  name: string,
  unitPrice: number,
  quantity: number = 1,
) => {
  trackEvent({
    name: "addItemToBasket",
    payload: {
      id,
      name,
      quantity,
      price: {
        value: unitPrice,
        currency: basket.currency,
      },
    },
  });
};

const onAddNewItem = async (
  id: string,
  plu: string,
  unitPrice: number,
  name: string,
  maxSelection: number | null,
) => {
  try {
    const quantity = 1;

    await basket.addItem({
      id,
      plu,
      unitPrice,
      quantity,
      menuId: menu.value?.id,
      modifierLists: [],
      maxSelection,
    });

    trackAddItem(id, name, unitPrice, quantity);
  } catch (error) {
    handleItemError(error);
  }
};

const { goToItem } = useNavigation();

const onIncreaseItem = async (itemId: string) => {
  const item = basket.items.find((obj) => obj.itemId === itemId);

  if (!item) {
    const itemToBeAdded = menu.value?.categories
      .map((category) => category.items.find((item) => item.id === itemId))
      .find((obj) => obj !== undefined);

    if (itemToBeAdded === undefined) {
      return;
    }

    if (itemToBeAdded.hasModifier || itemToBeAdded.isCombo) {
      goToItem(itemId);
      return;
    }

    await onAddNewItem(
      itemId,
      itemToBeAdded.plu,
      itemToBeAdded.price.value,
      itemToBeAdded.name,
      itemToBeAdded.maxSelection,
    );
    return;
  }

  try {
    await basket.updateItemQuantity(item.lineId, item.quantity + 1);

    trackAddItem(item.itemId, item.name, item.unitPrice.value, 1);
  } catch (error) {
    handleItemError(error);
  }
};

const onDecreaseItem = async (itemId: string) => {
  const item = basket.items.find((obj) => obj.itemId === itemId);

  if (!item) {
    throw new Error("Item not found.");
  }

  try {
    await basket.updateItemQuantity(item.lineId, item.quantity - 1);

    trackEvent({
      name: "removeItemFromBasket",
      payload: {
        id: item.itemId,
        name: item.name,
        quantity: 1,
        price: item.unitPrice,
      },
    });
  } catch (error) {
    handleItemError(error);
  }
};
</script>

<template>
  <div :class="css()">
    <div v-if="isLoading" :class="css('loading')">
      <AtomLoadingSpinner />
    </div>
    <template v-else-if="menu">
      <div
        v-if="menu.icon && props.displayMenuCover"
        :class="css('banner')"
        :style="`background-image: url(${optimize(menu.icon, { w: 1100, fit: 'crop', crop: 'top' })});`"
      ></div>
      <div :class="css('content')">
        <div
          ref="navWrapper"
          :class="[
            css('nav-wrapper'),
            isSticky && css('nav-wrapper', 'sticky'),
            business.isMobileApp && css('nav-wrapper', 'top-sticky'),
          ]"
        >
          <div :class="css('nav')">
            <SatMenuNavigator
              :title="menu?.name"
              :items="menuPills"
              :active-id="pillCategoryActiveId"
              :searchable="!business.isMobileApp"
              @item-selected="scrollToSection"
              @search="(value) => (querySearch = value)"
            />
          </div>
        </div>
        <div v-if="menuFiltered.length">
          <AtomIntersection
            v-for="(category, index) in menuFiltered"
            :id="category.id"
            :ref="categoryRef(category.id)"
            :key="index + category.name"
            :class="{
              [css('category')]: true,
              [css('category', 'grid')]: business.isMenuGrid,
            }"
            @enter="onEnter"
          >
            <TptMenuCategory
              :key="index + category.name"
              v-model:is-open="openStates[category.id]"
              :is-disabled="basket.isProcessing"
              :name="category.name"
              :is-grid-layout="business.isMenuGrid"
              :view-only="menu.isViewOnly"
              :items="category.items"
              view-type="block"
              is-collapsible
              @click-item="goToItem"
              @increase-item="onIncreaseItem"
              @decrease-item="onDecreaseItem"
            />
          </AtomIntersection>
        </div>
        <div v-else :class="css('no-result')">
          <AtomText color="color-text-secondary" :class="css('no-result-text')">
            {{
              menu.categories.length
                ? t("components.menu_handler.search_not_found")
                : t("components.menu_handler.menu_empty")
            }}
          </AtomText>
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
$header-height: v-bind(headerHeightPixel);

.menu-handler {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);

  &__banner {
    width: 100%;
    height: 140px;
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center center;
    background-color: var(--color-base-90);
    display: none;
  }

  &__content {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);

    @media (width > $breakpoint-sm) {
      gap: var(--spacing-lg);
    }
  }

  &__nav-wrapper {
    min-height: 35px;
    background: var(--color-background-shell);
    padding-block: var(--spacing-sm);
    position: sticky;
    top: $header-height;
    z-index: 2;
    border-radius: var(--radii-card-md);

    @media (width >= $breakpoint-sm) {
      padding: var(--spacing-sm) var(--spacing-md);
    }

    @media (width >= $breakpoint-lg) {
      border-radius: var(--radii-card-md);
    }

    &--sticky {
      @media (width >= $breakpoint-sm) {
        border-radius: 0 0 var(--radii-card-md) var(--radii-card-md);
      }
    }

    &--top-sticky {
      top: 0;
    }
  }

  &__nav {
    width: 100%;
    padding-inline: var(--spacing-md);

    @media (width > $breakpoint-sm) {
      padding-inline: 0;
    }
  }

  &__category {
    margin-bottom: var(--spacing-2xl);

    &--grid {
      margin-bottom: 0;

      @media (width > $breakpoint-sm) {
        margin-bottom: var(--spacing-2xl);
      }
    }

    &:last-child {
      @media (width > $breakpoint-sm) {
        margin-bottom: 0;
      }
    }
  }

  &__search {
    margin-bottom: var(--spacing-sm);
  }

  &__no-result {
    min-height: 100px;
    display: flex;
    text-align: center;
    align-items: center;
    justify-content: center;
    padding: var(--spacing-md);
    border-radius: var(--radii-card-md);
    background: var(--color-base-100);

    &-text {
      max-width: 300px;
      padding-block: var(--spacing-sm);
    }
  }

  &__loading {
    display: flex;
    justify-content: center;
    padding: var(--spacing-2xl) 0;
    margin-bottom: calc(var(--spacing-4xl) * 4);
  }
}
</style>
